import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'welcome',
    category: 'getting-started',
    title: 'Benvenuto — come funziona la piattaforma',
    summary: 'Una rapida panoramica di ciò che fa la piattaforma e dei passi per ottenere un risultato.',
    body: (
      <>
        <p>
          La piattaforma EcoEmpower ti aiuta a pianificare e gestire l'energia della tua comunità —
          prevedendo quanta energia useranno gli edifici, ottimizzando quando usarla e
          stimando i costi e i benefici di investimenti come pannelli solari o batterie.
        </p>
        <p>Lavorare con la piattaforma segue cinque semplici passaggi:</p>
        <ol>
          <li><strong>Crea una comunità</strong> — assegnagli un nome, un paese e una valuta.</li>
          <li><strong>Aggiungi edifici e asset</strong> — gli edifici della tua comunità e qualsiasi
            impianto come pannelli solari, batterie o caricatori per veicoli elettrici.</li>
          <li><strong>Carica i dati</strong> — file energetici storici (file CSV) che descrivono
            quanta energia utilizza ogni edificio, quanta ne producono i tuoi impianti solari/eolici e i prezzi dell'elettricità.</li>
          <li><strong>Esegui gli strumenti</strong> — Previsioni, Ottimizzazione e Pianificazione a Lungo Termine.</li>
          <li><strong>Visualizza i risultati</strong> — grafici e dati che puoi salvare ed esportare.</li>
        </ol>
        <Tip>
          Gli strumenti funzionano meglio con dati reali, ma puoi esplorare la piattaforma anche prima di averli —
          la maggior parte degli strumenti usa risultati di esempio illustrativi, quindi nulla è bloccato.
        </Tip>
        <ScreenshotPlaceholder
          img="dashboard-overview.png"
          description="The main Dashboard after logging in, showing the community count cards and the three action cards (Communities, Quick Start, Documentation)."
          source="Platform — log in and capture the main Dashboard (the page at the root URL, e.g. http://localhost/)."
          caption="Il Pannello è il tuo punto di partenza."
        />
        <h5 className="mt-4">Il ruolo dei dati</h5>
        <p>
          Il compito singolo più importante — e più comune — è caricare correttamente i tuoi file di dati.
          La sezione <strong>Preparazione e caricamento dei dati</strong> di questa guida spiega esattamente
          come farlo, con modelli pronti all'uso. Se un caricamento viene rifiutato, la sezione
          <strong> Risoluzione dei problemi</strong> spiega ogni messaggio di errore in modo chiaro.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-overview',
    category: 'data',
    title: 'Di quali file di dati ho bisogno?',
    summary: "L'elenco completo dei file di dati, a cosa serve ciascuno e quando è necessario.",
    body: (
      <>
        <p>
          I dati vengono caricati come <strong>file CSV</strong> — semplici file di foglio di calcolo che puoi
          creare in Excel o Google Sheets. Esistono due gruppi:
        </p>
        <ul>
          <li><strong>Dati dell'edificio</strong> — caricati per un edificio specifico (il suo consumo energetico, il suo FV su tetto, le sue bollette).</li>
          <li><strong>Dati della comunità</strong> — caricati una volta per l'intera comunità (un impianto solare condiviso, una turbina eolica, prezzi dell'elettricità, meteo).</li>
        </ul>

        <h5 className="mt-4">File di dati dell'edificio</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>File</th><th>Colonne (obbligatorie)</th><th>A cosa serve</th></tr>
          </thead>
          <tbody>
            <tr><td>Profilo di carico dell'edificio</td><td><Code>timestamp</Code>, <Code>consumption_kwh</Code></td><td>Quanta elettricità usa l'edificio ogni ora — necessario per le previsioni e l'ottimizzazione.</td></tr>
            <tr><td>Generazione FV dell'edificio</td><td><Code>timestamp</Code>, <Code>generation_kwh</Code></td><td>Quanta energia ha prodotto ogni ora il FV su tetto dell'edificio.</td></tr>
            <tr><td>Bolletta energetica dell'edificio</td><td><Code>period_start</Code>, <Code>period_end</Code>, <Code>consumption_kwh</Code>, <Code>cost_eur</Code></td><td>Bollette mensili — utilizzate per l'analisi dei costi.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">File di dati della comunità</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>File</th><th>Colonne (obbligatorie)</th><th>A cosa serve</th></tr>
          </thead>
          <tbody>
            <tr><td>Profilo di generazione FV</td><td><Code>timestamp</Code>, <Code>generation_kw</Code></td><td>Produzione di un impianto solare condiviso dalla comunità.</td></tr>
            <tr><td>Profilo di generazione eolica</td><td><Code>timestamp</Code>, <Code>generation_kw</Code></td><td>Produzione di una turbina eolica comunitaria.</td></tr>
            <tr><td>Tariffa storica di importazione</td><td><Code>timestamp</Code>, <Code>import_price_eur_kwh</Code></td><td>Il prezzo pagato per acquistare l'elettricità, ora per ora, nel corso di un anno.</td></tr>
            <tr><td>Tariffa storica di esportazione</td><td><Code>timestamp</Code>, <Code>export_price_eur_kwh</Code></td><td>Il prezzo ricevuto per vendere l'elettricità alla rete.</td></tr>
            <tr><td>Tariffa di importazione/esportazione Day-Ahead</td><td>come sopra</td><td>I prezzi orari del giorno successivo (24 righe) — caricati ogni giorno per l'ottimizzazione Day-Ahead.</td></tr>
            <tr><td>Meteo solare</td><td><Code>timestamp</Code>, <Code>ghi_w_m2</Code></td><td>Irraggiamento solare e temperatura — migliora le previsioni solari.</td></tr>
            <tr><td>Meteo eolico</td><td><Code>timestamp</Code>, <Code>wind_speed_m_s</Code></td><td>Velocità del vento — migliora le previsioni eoliche.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Da dove provengono questi dati?</h5>
        <p>Di solito non si inseriscono questi dati a mano — li si esporta da un sistema già in uso:</p>
        <ul>
          <li><strong>Consumo dell'edificio</strong> — dal portale online del contatore intelligente dell'edificio,
            dal tuo operatore di distribuzione (DSO) o dall'area clienti del tuo fornitore di elettricità.</li>
          <li><strong>Generazione solare/eolica</strong> — dall'app di monitoraggio o dal portale web
            dell'inverter o della turbina (di solito configurato dall'installatore).</li>
          <li><strong>Tariffe</strong> — dal tuo contratto elettrico o fornitore; i prezzi Day-Ahead
            provengono dalla borsa elettrica nazionale o, ancora, dal tuo fornitore.</li>
          <li><strong>Meteo</strong> — da un servizio meteorologico nazionale o da un fornitore gratuito
            di dati meteo. Questo file è opzionale.</li>
        </ul>
        <p>
          Questi sistemi di solito ti permettono di esportare un intervallo di date selezionato come file CSV o Excel. Una volta
          ottenuto il file, i prossimi due articoli mostrano come portarlo nella forma corretta.
        </p>

        <Tip>
          Non hai bisogno di <strong>tutti</strong> i file per iniziare. L'essenziale è un
          <strong> Profilo di carico dell'edificio</strong> per ogni edificio e un file di <strong>tariffa</strong>.
          Aggiungi i file di generazione e meteo in seguito per migliorare la precisione.
        </Tip>
        <p className="mt-3">
          Ogni tipo di file ha il suo breve articolo in questa sezione, con un esempio e un
          modello scaricabile. Inizia con <em>«Creare file CSV correttamente»</em> qui sotto — evita
          la grande maggioranza dei problemi di caricamento.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-csv-basics',
    category: 'data',
    title: 'Creare file CSV correttamente (Excel e Google Sheets)',
    summary: 'Le regole fondamentali per un file valido — e il principale errore da evitare.',
    body: (
      <>
        <p>
          Un <strong>file CSV</strong> è semplicemente un foglio di calcolo salvato in un semplice formato di testo.
          Lo prepari in Excel o Google Sheets come qualsiasi foglio di calcolo, poi lo salvi come CSV.
          Segui queste regole e i tuoi caricamenti funzioneranno al primo tentativo.
        </p>

        <h5 className="mt-4">Le regole fondamentali</h5>
        <ol>
          <li><strong>Prima riga = intestazioni delle colonne.</strong> La prima riga deve contenere i nomi esatti
            delle colonne per il tipo di file (ad esempio <Code>timestamp</Code> e <Code>consumption_kwh</Code>).
            L'ortografia e le lettere minuscole devono corrispondere esattamente.</li>
          <li><strong>Una riga per ora.</strong> Ogni riga successiva rappresenta un'ora di dati. Un anno completo
            è <strong>8.760 righe</strong> (365 giorni × 24 ore), più la riga di intestazione.</li>
          <li><strong>Usa il formato timestamp</strong> <Code>2025-01-01T14:00:00Z</Code> — ovvero
            Anno-Mese-Giorno, la lettera <Code>T</Code>, poi Ora:Minuto:Secondo, poi la lettera <Code>Z</Code>.</li>
          <li><strong>I numeri usano il punto, mai la virgola.</strong> Scrivi <Code>3.5</Code>, non <Code>3,5</Code>.</li>
          <li><strong>Nessuna riga vuota, nessun totale, nessuna nota aggiuntiva</strong> — solo la riga di intestazione e le righe di dati.</li>
        </ol>

        <Warn>
          <strong>L'errore più comune.</strong> In Italia, Francia, Germania, Cechia e Grecia,
          i programmi di foglio di calcolo normalmente scrivono i numeri decimali con una <strong>virgola</strong>
          (<Code>0,457</Code>). La piattaforma si aspetta un <strong>punto</strong> (<Code>0.457</Code>), quindi
          un file con virgole viene rifiutato con «Value must be a number». Se il tuo file ha già le virgole,
          <strong> non</strong> è necessario riscriverlo — vedi{' '}
          <ArticleLink to="data-fix-commas">Correggere un file che usa le virgole come separatore decimale</ArticleLink>.
        </Warn>

        <h5 className="mt-4">Informazioni sulla colonna timestamp</h5>
        <p>
          Il formato più affidabile è <Code>2025-01-01T14:00:00Z</Code>, e ha un vantaggio nascosto:
          Excel tende a riformattare silenziosamente qualsiasi cosa riconosca come data, ma di solito
          lascia invariata questa forma grazie alla <Code>T</Code> e alla <Code>Z</Code>. La
          piattaforma comprende anche i comuni formati europei come <Code>01/01/2025 14:00</Code>
          e <Code>2025-01-01</Code> — ma sono esattamente quelli che Excel potrebbe modificare
          senza che tu te ne accorga.
        </p>
        <p>
          Se i tuoi timestamp sono stati alterati, o devi costruire la colonna, inserisci questa formula in una
          colonna libera (qui la data/ora originale è nella cella <Code>A2</Code>):
        </p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`=TEXT(A2,"yyyy-mm-ddThh:mm:ss")&"Z"`}
        </pre>
        <p>
          Copiala in ogni riga, poi seleziona la colonna e usa
          <strong> Copia → Incolla speciale → Valori</strong> per fissare il risultato. Usa quella colonna come
          tuo <Code>timestamp</Code>.
        </p>

        <h5 className="mt-4">Quante righe? (risoluzione dei dati)</h5>
        <p>
          Una riga per ogni periodo di tempo. I dati <strong>orari</strong> sono tipici — un anno completo è
          <strong> 8.760 righe</strong> — ma vengono accettati anche dati a <strong>15 minuti</strong> e <strong>giornalieri</strong>.
          È necessario un minimo di 24 righe (un giorno).
        </p>

        <h5 className="mt-4">Salvare come CSV da Microsoft Excel</h5>
        <ol>
          <li>Fai clic su <strong>File → Salva con nome</strong>.</li>
          <li>Nel menu a discesa del tipo di file scegli <strong>«CSV UTF-8 (delimitato da virgole) (*.csv)»</strong>.</li>
          <li>Fai clic su <strong>Salva</strong>. Se Excel avverte che alcune funzionalità andranno perse, fai clic su <strong>Sì</strong> — è normale.</li>
        </ol>
        <p className="small text-muted">
          Se la tua versione di Excel non riporta «CSV UTF-8», scegli invece «CSV (delimitato da virgole)».
        </p>
        <ScreenshotPlaceholder
          img="excel-save-as-csv.png"
          description="Excel's Save As dialog with the file-type dropdown open, highlighting the 'CSV UTF-8 (Comma delimited)' option."
          source="Microsoft Excel — open any workbook → File → Save As → click the file-type dropdown. Capture the dropdown open with the CSV UTF-8 entry highlighted."
          caption="In Excel, scegli sempre «CSV UTF-8»."
        />

        <h5 className="mt-4">Salvare come CSV da Google Sheets</h5>
        <ol>
          <li>Fai clic su <strong>File → Scarica → Valori separati da virgola (.csv)</strong>.</li>
          <li>Il file viene salvato nella cartella Download del tuo computer.</li>
        </ol>
        <ScreenshotPlaceholder
          img="gsheets-download-csv.png"
          description="Google Sheets File → Download submenu, highlighting 'Comma-separated values (.csv)'."
          source="Google Sheets (sheets.google.com) — open any spreadsheet → File → Download. Capture the submenu open with 'Comma-separated values (.csv)' highlighted."
          caption="In Google Sheets, usa File → Scarica → .csv."
        />

        <h5 className="mt-4">Verifica il file prima di caricarlo</h5>
        <p>
          Riapri il file <Code>.csv</Code> salvato e conferma i punti seguenti. Per essere
          completamente sicuro, aprilo con un editor di testo semplice (<strong>Notepad</strong> su Windows,
          <strong> TextEdit</strong> su Mac) invece di un foglio di calcolo — un editor di testo mostra il
          vero contenuto del file, mentre Excel riapplica la formattazione locale sullo schermo e può nascondere
          il vero problema.
        </p>
        <p>Verifica che:</p>
        <ul>
          <li>La prima riga mostri i nomi di colonna corretti.</li>
          <li>I numeri abbiano l'aspetto di <Code>3.5</Code> (punto), non <Code>3,5</Code> (virgola).</li>
          <li>I timestamp abbiano l'aspetto di <Code>2025-01-01T14:00:00Z</Code>.</li>
          <li>Non ci siano righe vuote o righe di riepilogo in fondo.</li>
        </ul>
        <Tip>
          Il modo più semplice: scarica un modello da uno degli articoli specifici per tipo di file in questa
          sezione, sostituisci i numeri di esempio con i tuoi e salva. Il modello ha già
          le intestazioni corrette e il formato numerico giusto.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-fix-commas',
    category: 'data',
    title: 'Correggere un file che usa le virgole come separatore decimale',
    summary: 'Converti un file con numeri a virgola (3,5) nel formato a punto richiesto dalla piattaforma (3.5) — senza riscrivere nulla.',
    body: (
      <>
        <p>
          I programmi di foglio di calcolo in Italia, Francia, Germania, Cechia e Grecia normalmente scrivono
          i numeri decimali con una <strong>virgola</strong> — <Code>0,457</Code> invece di
          {' '}<Code>0.457</Code>. La piattaforma richiede il formato con il punto. La buona notizia: non
          {' '}<strong>è necessario</strong> riscrivere nulla. Modifica un'impostazione, salva di nuovo il file
          e hai finito. Usa il metodo che corrisponde al programma che hai.
        </p>

        <h5 className="mt-3">Metodo 1 — Google Sheets (il più semplice, gratuito)</h5>
        <ol>
          <li>Vai su <strong>sheets.google.com</strong> e apri un foglio di calcolo vuoto.</li>
          <li>Scegli <strong>File → Importa → Carica</strong> e seleziona il tuo file CSV.</li>
          <li>Scegli <strong>File → Impostazioni</strong>. Sotto <strong>Impostazioni internazionali</strong>, seleziona
            {' '}<strong>Stati Uniti</strong> (o Regno Unito), poi <strong>Salva impostazioni</strong>.
            Questo indica a Google Sheets di usare il punto per i decimali.</li>
          <li>Scegli <strong>File → Scarica → Valori separati da virgola (.csv)</strong>.</li>
        </ol>
        <p>Il file scaricato ora usa i punti ed è pronto per essere caricato.</p>
        <ScreenshotPlaceholder
          img="gsheets-locale-setting.png"
          description="Google Sheets — the File → Settings dialog with the Locale dropdown open and 'United States' highlighted."
          source="Google Sheets (sheets.google.com) — open any spreadsheet → File → Settings → the General tab. Capture the modal with the Locale dropdown open and 'United States' highlighted."
          caption="Impostare le Impostazioni internazionali su Stati Uniti fa esportare i punti a Google Sheets."
        />

        <h5 className="mt-4">Metodo 2 — Microsoft Excel</h5>
        <ol>
          <li>Apri il file in Excel, poi vai su <strong>File → Opzioni → Avanzate</strong>.</li>
          <li>Sotto <strong>Opzioni di modifica</strong>, deseleziona <strong>«Usa separatori di sistema»</strong>.</li>
          <li>Imposta il <strong>Separatore decimale</strong> su un punto <Code>.</Code> e
            {' '}il <strong>Separatore delle migliaia</strong> su uno spazio singolo (o lascialo vuoto).</li>
          <li>Fai clic su <strong>OK</strong>, poi usa <strong>File → Salva con nome</strong> e scegli
            {' '}<strong>«CSV UTF-8 (delimitato da virgole)»</strong>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="excel-separator-options.png"
          description="Excel — File → Options → Advanced, the 'Editing options' area, with 'Use system separators' unticked and the decimal/thousands separator boxes visible."
          source="Microsoft Excel — open any workbook → File → Options → Advanced tab → scroll to 'Editing options'. Capture the section with the 'Use system separators' checkbox unticked."
          caption="In Excel, disattiva i separatori di sistema e imposta il separatore decimale su un punto."
        />
        <Tip>Questa impostazione riguarda tutto Excel. Puoi riattivare «Usa separatori di sistema» in seguito.</Tip>

        <h5 className="mt-4">Metodo 3 — Trova e sostituisci (ultima risorsa)</h5>
        <p>
          Usa questo metodo solo se i numeri sono memorizzati come testo normale e il file <strong>non</strong>
          {' '}contiene separatori delle migliaia (nulla nel formato <Code>1.234,5</Code>), perché una
          sostituzione indiscriminata danneggerebbe anche quelli.
        </p>
        <ol>
          <li>Seleziona solo le colonne che contengono numeri.</li>
          <li>Apri <strong>Trova e sostituisci</strong> (Ctrl+H), cerca <Code>,</Code>, sostituisci con
            {' '}<Code>.</Code>, poi Sostituisci tutto.</li>
          <li>Controlla alcuni valori prima di salvare.</li>
        </ol>

        <h5 className="mt-4">E gli strumenti di conversione online?</h5>
        <p>
          Esistono siti web gratuiti di «conversione CSV», ma richiedono di caricare il file su un sito
          di terze parti, e i dati energetici possono essere sensibili. Consigliamo i metodi sopra descritti —
          usano software che già possiedi e mantengono il file sul tuo computer. Se la tua organizzazione
          usa già Google Workspace, il Metodo 1 rimane all'interno degli strumenti di cui ti fidi.
        </p>

        <Warn>
          Un file <Code>.csv</Code> aperto in Excel può <em>sembrare</em> che abbia ancora le virgole anche
          quando è stato salvato con i punti, perché Excel riapplica la formattazione locale sullo schermo. Per
          vedere cosa c'è <strong>davvero</strong> nel file, aprilo con un editor di testo semplice —
          {' '}<strong>Notepad</strong> su Windows o <strong>TextEdit</strong> su Mac. Lì, i
          numeri dovrebbero essere <Code>3.5</Code>.
        </Warn>
      </>
    ),
  },
]
